import { SubscriptionIntervals, type SubscriptionPlans } from "@prisma/client";

import {
  STRIPE_PRICE_ID_TO_INTERVAL_MAP,
  STRIPE_PRICE_ID_TO_PLAN_MAP,
  getStripePriceIdForPlan,
  isStripeConsumerPriceId,
  type TStripePriceId,
} from "@musicfy/contants/StripeSubscriptions";
import {
  PRICE_ID_TO_INTERVAL_MAP,
  PRICE_ID_TO_PLAN_MAP,
  SubscriptionValueMap,
  type AvailablePlans,
  type TPriceId,
} from "@musicfy/contants/Subscriptions";

export const StripePlanChangeKind = {
  upgrade: "upgrade",
  downgrade: "downgrade",
  noop: "noop",
} as const;

export type TStripePlanChangeKind =
  (typeof StripePlanChangeKind)[keyof typeof StripePlanChangeKind];

export type TStripePlanChangeTarget = {
  plan: SubscriptionPlans;
  interval: SubscriptionIntervals;
};

export type StripeChangePreview = {
  kind: Exclude<TStripePlanChangeKind, typeof StripePlanChangeKind.noop>;
  currentPlan: SubscriptionPlans;
  currentInterval: SubscriptionIntervals;
  targetPlan: SubscriptionPlans;
  targetInterval: SubscriptionIntervals;
  targetPriceId: TStripePriceId;
  /** ISO date when downgrade applies; null for upgrades */
  effectiveAt: string | null;
  /** Amount due today in cents (upgrade proration). 0 for downgrade. */
  amountDueTodayCents: number;
  currency: string;
  recurringAmountCents: number;
};

/**
 * Classify a plan/interval change for Stripe self-serve billing.
 * Higher plan value = upgrade (immediate). Lower = downgrade (period end).
 * Same plan: month→year = upgrade; year→month = downgrade.
 */
export const classifyStripePlanChange = (
  current: TStripePlanChangeTarget,
  target: TStripePlanChangeTarget
): TStripePlanChangeKind => {
  if (current.plan === target.plan && current.interval === target.interval) {
    return StripePlanChangeKind.noop;
  }

  const currentValue = SubscriptionValueMap[current.plan];
  const targetValue = SubscriptionValueMap[target.plan];

  if (targetValue > currentValue) {
    return StripePlanChangeKind.upgrade;
  }

  if (targetValue < currentValue) {
    return StripePlanChangeKind.downgrade;
  }

  if (
    target.interval === SubscriptionIntervals.year &&
    current.interval === SubscriptionIntervals.month
  ) {
    return StripePlanChangeKind.upgrade;
  }

  if (
    target.interval === SubscriptionIntervals.month &&
    current.interval === SubscriptionIntervals.year
  ) {
    return StripePlanChangeKind.downgrade;
  }

  return StripePlanChangeKind.noop;
};

export const STRIPE_RENEW_BEFORE_CHANGE_MESSAGE =
  "Your subscription is set to cancel at the end of the billing period. Keep your subscription first (Billing → Keep Subscription), then change your plan.";

export const clearPendingPlanChangeData = {
  pendingPlan: null,
  pendingInterval: null,
  pendingChangeAt: null,
  stripeScheduleId: null,
} as const;

/**
 * Pricing UI still uses Paddle price IDs; confirm/change may send Stripe IDs.
 * Accept either so plan-change never fails with a confusing "Invalid plan price".
 */
export const resolveStripeChangeTargetFromPriceId = (
  priceId: string
): {
  plan: AvailablePlans;
  interval: SubscriptionIntervals;
  stripePriceId: TStripePriceId;
} | null => {
  if (isStripeConsumerPriceId(priceId)) {
    return {
      plan: STRIPE_PRICE_ID_TO_PLAN_MAP[priceId],
      interval: STRIPE_PRICE_ID_TO_INTERVAL_MAP[priceId],
      stripePriceId: priceId,
    };
  }

  const paddlePriceId = priceId as TPriceId;
  const plan = PRICE_ID_TO_PLAN_MAP[paddlePriceId];
  const interval = PRICE_ID_TO_INTERVAL_MAP[paddlePriceId];
  if (!plan || !interval) {
    return null;
  }

  return {
    plan,
    interval,
    stripePriceId: getStripePriceIdForPlan(plan, interval),
  };
};
